import React from 'react';
import { 
  Dna, 
  FileText, 
  Network,
  Info,
  Activity
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NoResults = ({ searchTerm }) => {
  const navigate = useNavigate();

  const suggestionOptions = [
    {
      title: 'Gene Search',
      description: searchTerm 
        ? `Search "${searchTerm}" for gene information`
        : 'Explore gene information',
      icon: Dna,
      longDescription: 'Search genes by name (e.g., "DLG4") or Entrez ID (e.g., "1742") to access related diseases, mutations, and proteomic data. Gene Search integrates Online Mendelian Inheritance in Man (OMIM), International Classification of Diseases (ICD-11), Human Phenotype Ontology (HPO), Gene Ontology (GO), Synaptic Gene Ontology (SynGO), and PubMed to provide detailed functional and clinical insights with filters and keyword highlighting.',


      action: () => {
        navigate(searchTerm 
          ? `/gene-search?q=${encodeURIComponent(searchTerm)}` 
          : '/gene-search');
      }
    },
    {
      title: 'Disease Search',
      description: searchTerm 
        ? `Search "${searchTerm}" for disease information`
        : 'Explore disease information',
      icon: Activity,
      longDescription: 'With Disease Search, uncover connections between diseases and genes. Enter a disease name (e.g., "Alzheimer\'s"), Human Disease Ontology Identifier (HDOID), or description to find related genes, mutations, and synapse proteomic papers. Filter genes by consensus status or protein names (e.g., "PSD95"), and explore detailed paper lists in a modal view for deeper insights.',
      action: () => {
        navigate(searchTerm 
          ? `/disease-search?q=${encodeURIComponent(searchTerm)}` 
          : '/disease-search');
      }
    },
    {
      title: 'Publication Search',
      description: searchTerm 
        ? `Search "${searchTerm}" for publication information`
        : 'Search proteomic research publications',
      icon: FileText,
      longDescription: 'The Synapse Proteomic Publication Search option lets you explore synapse proteomic research publications. Search by title, PubMed ID (e.g., "12345678"), year, or description to access publications and their associated genes and mutations. Apply filters for gene localization or consensus status to refine your results and link directly to PubMed for full texts.',
      action: () => {
        navigate(searchTerm 
          ? `/paper-search?q=${encodeURIComponent(searchTerm)}` 
          : '/paper-search');
      }
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="max-w-xl mb-8">
        {searchTerm ? (
          <h2 className="text-2xl font-bold mb-3 text-gray-800">
            "{searchTerm}"
          </h2>
        ) : (
          <h2 className="text-2xl font-bold mb-3 text-gray-800">
            Enter a search term to find results
          </h2>
        )}
        
        <p className="text-gray-600 text-lg">
          {searchTerm 
            ? 'Try these alternative search options with your term:'
            : 'Explore these search options:'}
        </p>
      </div>
      
      {/* Search Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-6xl mb-12">
        {suggestionOptions.map((option, index) => {
          const Icon = option.icon;
          return (
            <div key={index} className="flex flex-col h-full">
              <button 
                className="flex flex-col items-center justify-center p-6 border rounded-t-lg hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 space-y-3 cursor-pointer bg-white shadow-sm"
                onClick={option.action}
              >
                <Icon className="w-10 h-10 text-blue-600 mb-2" />
                <div className="font-bold text-lg text-gray-800">{option.title}</div>
                <div className="text-sm text-gray-600 text-center">
                  {option.description}
                </div>
              </button>
              
              {/* Description Panel */}
              <div className="p-4 bg-blue-50 border border-t-0 rounded-b-lg">
                <div className="flex items-start space-x-2">
                  <Info className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-left text-gray-700 leading-relaxed">
                    {option.longDescription}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Additional Resources Section
      <div className="w-full max-w-4xl bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Need Help?</h3>
        <p className="text-gray-700 mb-4">
          If you're having trouble finding what you need, try refining your search terms or use one of our guided search options above. Each search type provides specialized results for different research needs.
        </p>
        <div className="flex justify-center space-x-4">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
            View Tutorial
          </button>
          <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 transition-colors duration-200">
            Contact Support
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default NoResults;